import React, { useContext, useEffect, useRef, useState } from "react"
import styled from "@emotion/styled"
import { graphql, navigate, StaticQuery } from "gatsby"
import { Flex, Text } from "theme-ui"

import { InterfaceContext } from "../../context/interface-context"
import Button from "../base/buttons"
import Input from "../base/input-field"
import Cross from "../../assets/icons/cross.svg"
import SearchIcon from "../../assets/icons/search-new.svg"
import Image from "../base/image"
import { useRecentlyViewed } from "../../context/recently-viewed-context"

const SearchContainer = styled(Flex)`
  background-color: ${(props) => props.theme.colors.white};
  flex-direction: column;
  position: fixed;
  height: 100%;
  z-index: 1021;
  transition: transform 0.5s ease-out;
  transform: translateX(100%);
  top: 0;
  right: 0;
  overflow-y: auto;
  max-width: 500px;
  width: 100%;
  padding-block: 45px;
  padding-inline: 30px;

  ${(props) =>
    props.visible &&
    `
      transform: translateX(0);
    `}
`

const MiddleNavigation = styled(Flex)`
  flex-direction: column;
  color: ${({ theme }) => theme.colors.darkerGreen};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  margin-bottom: 42px;
`

const MiddleNavigationHeading = styled(Text)`
  font-family: ${({ theme }) => theme.fonts.body};
  text-transform: uppercase;
  margin-bottom: 16px;
`

const MiddleNavigationItem = styled(Text)`
  margin-bottom: 5px;
  cursor: pointer;

  &:last-of-type {
    margin-bottom: 0;
  }
`

const Footer = styled(Flex)`
  flex-direction: column;
  color: ${({ theme }) => theme.colors.darkerGreen};
`

const FooterHeading = styled(Text)`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  text-transform: uppercase;
  margin-bottom: 25px;
`

const FooterList = styled(Flex)`
  gap: 30px;
  flex-direction: column;

  ${({ theme }) => theme.bp.sm} {
    flex-direction: row;
  }
`

const FooterItem = styled.div`
  flex: 1;

  & img {
    margin-bottom: 10px;
    width: 100%;
    height: 264px;
  }

  & .footer-item-title,
  & .footer-item-price {
    font-size: ${({ theme }) => theme.fontSizes[1]}px;
    color: ${({ theme }) => theme.colors.darkerGreen};
    display: block;
  }
`

const SearchDrawer = () => {
  const { isSearchOpen, hideSearchDrawer, hideDrawers } =
    useContext(InterfaceContext)
  const containerRef = useRef()
  const inputRef = useRef()
  //const [items, setItems] = useState()
  const [search, setSearch] = useState("")
  //const { products } = useStaticQuery(pageQuery)

  // useEffect(() => {
  //   if (products) {
  //     setItems({ all: products.nodes, visible: products.nodes.slice(0, 5) })
  //   }
  // }, [products])
  const { recentlyViewed } = useRecentlyViewed()

  useEffect(() => {
    if (!isSearchOpen) {
      return
    }

    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        hideDrawers()
      }
    }

    const escFunction = (event) => {
      if (event.key === "Escape" || event.key === "Enter") {
        hideDrawers()
      }
    }

    document.addEventListener("click", handleOutsideClick, true)
    document.addEventListener("keydown", escFunction, true)

    return () => {
      document.removeEventListener("click", handleOutsideClick, true)
      document.removeEventListener("keydown", escFunction, true)
    }
  }, [isSearchOpen, hideDrawers])

  const handleSearch = (e) => {
    let searchValue = e.target.value
    setSearch(searchValue)
    navigate(`/search?q=${searchValue}`)
  }

  const handleClick = (node) => {
    if (node.slug) {
      hideDrawers()
      navigate(`/${node.slug}`)
    } else if (typeof node === "string") {
      hideDrawers()
      navigate(node)
    }
  }

  return (
    <StaticQuery
      query={graphql`
        {
          suggestedSearches: contentfulNavigationMenu(
            name: { eq: "Suggested Searches" }
          ) {
            items {
              ... on ContentfulCategory {
                name
                slug
                id
              }
            }
            name
            id
          }
        }
      `}
      render={({ suggestedSearches }) => (
        <SearchContainer visible={isSearchOpen} ref={containerRef}>
          <Flex sx={{ gap: "29px", marginBottom: "46px" }}>
            <Flex
              sx={{
                position: "relative",
                width: "100%",
                borderBottom: "1px solid",
                paddingBottom: "5px",
              }}
            >
              <Button
                variant="option"
                sx={{
                  border: "none",
                  padding: "0 !important",
                }}
                onClick={() => {
                  hideSearchDrawer()
                  navigate(`/search?q=${search}`)
                }}
              >
                <SearchIcon />
              </Button>
              <Input
                placeholder="What are your looking for?"
                onChange={handleSearch}
                ref={inputRef}
                sx={{
                  border: "none",
                  fontSize: "14px",
                }}
                autoFocus
              />
            </Flex>
            <Button
              variant="option"
              sx={{
                flexShrink: 0,
                borderRadius: "100%",
                height: "44px",
                width: "44px",
                svg: { width: "14px" },
              }}
              onClick={() => {
                hideSearchDrawer()
              }}
            >
              <Cross />
            </Button>
          </Flex>

          <MiddleNavigation>
            <MiddleNavigationHeading>
              Suggested searches
            </MiddleNavigationHeading>
            {suggestedSearches.items.map((item) => (
              <MiddleNavigationItem onClick={() => handleClick(item)}>
                {item.name}
              </MiddleNavigationItem>
            ))}
          </MiddleNavigation>

          <Footer>
            <FooterHeading>Recently viewed</FooterHeading>
            <FooterList>
              {recentlyViewed.map((p) => (
                <FooterItem
                  onClick={() => {
                    handleClick(`/${p.catSlug}/${p.handle}`)
                  }}
                >
                  <Image primary={p.thumbnail && p.thumbnail} />
                  <Text className="footer-item-title">{p.title}</Text>
                  <Text className="footer-item-price">{p.price}</Text>
                </FooterItem>
              ))}
            </FooterList>
          </Footer>
        </SearchContainer>
      )}
    />
  )
}

export default SearchDrawer
